import React, { useEffect, useRef, useState } from "react";
import { api, API, getToken, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Moon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Copy, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { audioProfileOf, cdnOf } from "./audioProfile";
import HlsInputPanel from "./HlsInputPanel";
import AudioProfilePanel from "./AudioProfilePanel";
import LanguageTracksPanel from "./LanguageTracksPanel";
import OutputSignalsPanel from "./OutputSignalsPanel";
import CdnPipelinePanel from "./CdnPipelinePanel";
import CdnRegionsPanel from "./CdnRegionsPanel";
import EngineClusterPanel from "./EngineClusterPanel";
import KmsRotationPanel from "./KmsRotationPanel";
import EngineAdapterPanel from "./EngineAdapterPanel";
import Ac3InspectorPanel from "./Ac3InspectorPanel";
import ObsIngestPanel from "./ObsIngestPanel";
import CustomCdnPanel from "./CustomCdnPanel";
import GraphicsEditorDialog from "./GraphicsEditorDialog";
import useChannelSettings from "./useChannelSettings";

const DEFAULT_DV = { enabled: false, profile: "8.1", l1_dynamic_metadata: true, l6_max_cll_nits: 1000, l6_max_fall_nits: 400, master_display_min_nits: 0.005, master_display_max_nits: 4000, color_primaries: "BT.2020", transfer: "PQ", rpu_injection: "mp4", cm_version: "v4.0" };
const DEFAULT_HLG = { enabled: false, lut_id: "hlg_bt2020_default", lut_name: "HLG BT.2020 Default", hlg_system_gamma: 1.2, sdr_compat_lut: true, peak_luminance_nits: 1000 };
const DEFAULT_ENGINE = { adapter: "ffmpeg-nvenc", gpu_id: 0, encoder_codec: "H.264", encoder_preset: "llhq", keyframe_interval_sec: 2, b_frames: 3, lookahead_frames: 20, bitrate_ladder_kbps: [800, 1600, 3200, 6000], audio_encoder: "ac3", scte35_passthrough: true, low_latency_mode: true, engine_endpoint: "" };

const _PREVIEW_NOISE = new Set(["official","music","video","lyric","lyrics","audio","hd","4k","clip","officiel","visualizer","mv","hq","full"]);
function cleanPreviewTitle(name, artist) {
  let base = (name || "").replace(/\.[A-Za-z0-9]{2,4}$/, "").trim();
  base = base.replace(/[([{][^)\]}]*[)\]}]/g, " ");
  if (artist) {
    const pre = artist.toLowerCase() + " - ";
    if (base.toLowerCase().startsWith(pre)) base = base.slice(pre.length);
    else if (base.includes(" - ")) base = base.split(" - ").slice(1).join(" - ");
  }
  base = base.replace(/\s+/g, " ").replace(/^[\s\-_|]+|[\s\-_|]+$/g, "");
  let toks = base.split(" ");
  while (toks.length && _PREVIEW_NOISE.has(toks[toks.length - 1].toLowerCase().replace(/[^a-z0-9]/g, ""))) toks.pop();
  return toks.join(" ").replace(/^[\s\-_|]+|[\s\-_|]+$/g, "") || base;
}

/** Channel settings — orchestrates modular sub-panels. */
export default function ChannelSettingsDialog({ open, onClose, channel, onSaved }) {
  const [url, setUrl] = useState(channel?.preview_hls_url || "");
  const [demoUrl, setDemoUrl] = useState(channel?.demo_preview_hls_url || "");
  const [gapCat, setGapCat] = useState(channel?.gap_fallback_category_id || "");
  const [noRepeatMin, setNoRepeatMin] = useState(channel?.no_repeat_window_min ?? 120);
  const [cats, setCats] = useState([]);
  const [ap, setAp] = useState(audioProfileOf(channel));
  const [cdn, setCdn] = useState(cdnOf(channel));
  const [hdrMode, setHdrMode] = useState(channel?.hdr_mode || "SDR");
  const [dv, setDv] = useState(DEFAULT_DV);
  const [hlg, setHlg] = useState(DEFAULT_HLG);
  const [engine, setEngine] = useState(DEFAULT_ENGINE);
  const [saving, setSaving] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);
  const [inspect, setInspect] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canManageBranding = ["super_admin", "tenant_admin"].includes(user?.role);
  const [brandName, setBrandName] = useState(channel?.name || "");
  const [brandColor, setBrandColor] = useState(channel?.color || "#3B82F6");
  const [savingBrand, setSavingBrand] = useState(false);
  const [gfxOpen, setGfxOpen] = useState(false);
  const [logoPos, setLogoPos] = useState(channel?.logo_position || "top-right");
  const [logoOpacity, setLogoOpacity] = useState(channel?.logo_opacity ?? 0.9);
  const [logoSize, setLogoSize] = useState(channel?.logo_size_pct ?? 14);
  const [logoSafeMargin, setLogoSafeMargin] = useState(channel?.logo_safe_margin_pct ?? 0);
  const [brandPrimary, setBrandPrimary] = useState(channel?.brand_primary_color || "#7C3AED");
  const [brandSecondary, setBrandSecondary] = useState(channel?.brand_secondary_color || "#111827");
  const [brandText, setBrandText] = useState(channel?.brand_text_color || "#FFFFFF");
  const [flashCount, setFlashCount] = useState(channel?.song_flash_count ?? 2);
  const [flashDur, setFlashDur] = useState(channel?.song_flash_duration_sec ?? 10);
  const [flashStyle, setFlashStyle] = useState(channel?.song_flash_style || "fade");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [livePreview, setLivePreview] = useState(null); // {category, song} from current-hls

  useEffect(() => {
    if (!open) return;
    setFlashCount(channel?.song_flash_count ?? 2);
    setFlashDur(channel?.song_flash_duration_sec ?? 10);
    setFlashStyle(channel?.song_flash_style || "fade");
    api.get("/overlay-presets").then((r) => setPresets(r.data || [])).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, [open, channel?.id]);

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name your preset first"); return; }
    try {
      const r = await api.post("/overlay-presets", {
        name: presetName.trim(),
        brand_primary_color: brandPrimary, brand_secondary_color: brandSecondary, brand_text_color: brandText,
        logo_position: logoPos, logo_size_pct: Number(logoSize), logo_opacity: Number(logoOpacity),
        logo_safe_margin_pct: Number(logoSafeMargin),
        song_flash_count: Number(flashCount), song_flash_duration_sec: Number(flashDur), song_flash_style: flashStyle,
      });
      setPresets((p) => [...p, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setPresetName("");
      toast.success(`Preset "${r.data.name}" saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save preset"); }
  };

  const applyPreset = (p) => {
    if (p.brand_primary_color) setBrandPrimary(p.brand_primary_color);
    if (p.brand_secondary_color) setBrandSecondary(p.brand_secondary_color);
    if (p.brand_text_color) setBrandText(p.brand_text_color);
    if (p.logo_position) setLogoPos(p.logo_position);
    if (p.logo_size_pct != null) setLogoSize(p.logo_size_pct);
    if (p.logo_opacity != null) setLogoOpacity(p.logo_opacity);
    if (p.logo_safe_margin_pct != null) setLogoSafeMargin(p.logo_safe_margin_pct);
    if (p.song_flash_count != null) setFlashCount(p.song_flash_count);
    if (p.song_flash_duration_sec != null) setFlashDur(p.song_flash_duration_sec);
    if (p.song_flash_style) setFlashStyle(p.song_flash_style);
    toast.success(`Applied "${p.name}" — click Save brand to persist`);
  };

  const applyPresetAll = async (p) => {
    try {
      const r = await api.post(`/overlay-presets/${p.id}/apply`, { all_channels: true });
      toast.success(`"${p.name}" applied to ${r.data.applied_to} channel(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply-all failed"); }
  };

  const deletePreset = async (p) => {
    try {
      await api.delete(`/overlay-presets/${p.id}`);
      setPresets((prev) => prev.filter((x) => x.id !== p.id));
    } catch { toast.error("Delete failed"); }
  };

  const toggleDefault = async (p) => {
    const next = !p.is_default;
    try {
      await api.post(`/overlay-presets/${p.id}/set-default`, { is_default: next });
      setPresets((prev) => prev.map((x) => ({ ...x, is_default: x.id === p.id ? next : false })));
      toast.success(next ? `"${p.name}" is now the house default for new channels` : `Cleared default preset`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set default"); }
  };

  useEffect(() => {
    if (!open || !channel?.id) { setLivePreview(null); return; }
    let cancelled = false;
    api.get(`/cloudflare-stream/channel/${channel.id}/current-hls`)
      .then((r) => {
        if (cancelled || !r.data?.cdn) return;
        const t = cleanPreviewTitle(r.data.asset_name, r.data.artist_name);
        const bits = [r.data.artist_name, t, r.data.release_year ? String(r.data.release_year) : null].filter(Boolean);
        setLivePreview({
          category: r.data.category_name || null,
          song: bits.length ? bits.join(" — ") : null,
        });
      })
      .catch(() => { /* no live content — fall back to sample text */ });
    return () => { cancelled = true; };
  }, [open, channel?.id]);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const logoFileRef = useRef(null);
  const overnightFileRef = useRef(null);
  const [daypartsOn, setDaypartsOn] = useState(!!channel?.logo_dayparts_enabled);
  const [overnightStart, setOvernightStart] = useState(channel?.overnight_start || "00:00");
  const [overnightEnd, setOvernightEnd] = useState(channel?.overnight_end || "06:00");
  const [overnightOpacity, setOvernightOpacity] = useState(channel?.logo_overnight_opacity ?? 0.5);
  const [ovnPreviewUntil, setOvnPreviewUntil] = useState(channel?.logo_force_overnight_until || null);
  const [ovnPreviewBusy, setOvnPreviewBusy] = useState(false);
  const [scte35, setScte35] = useState(channel?.scte35_enabled ?? true);
  const [liveSourceMode, setLiveSourceMode] = useState(channel?.live_source_mode || "mp4");
  const [publicEmbed, setPublicEmbed] = useState(!!channel?.public_embed_enabled);
  const watchUrl = `${window.location.origin}/watch/${channel?.slug}`;

  const settings = useChannelSettings({ open, channel });

  // Sync form when a different channel is opened
  useEffect(() => {
    setUrl(channel?.preview_hls_url || "");
    setDemoUrl(channel?.demo_preview_hls_url || "");
    setGapCat(channel?.gap_fallback_category_id || "");
    setNoRepeatMin(channel?.no_repeat_window_min ?? 120);
    setAp(audioProfileOf(channel));
    setCdn(cdnOf(channel));
    setHdrMode(channel?.hdr_mode || "SDR");
    setDv({ ...DEFAULT_DV, ...(channel?.dolby_vision || {}) });
    setHlg({ ...DEFAULT_HLG, ...(channel?.hlg || {}) });
    setEngine((prev) => ({ ...prev, ...(channel?.engine || {}) }));
    setBrandName(channel?.name || "");
    setBrandColor(channel?.color || "#3B82F6");
    setLogoPos(channel?.logo_position || "top-right");
    setLogoOpacity(channel?.logo_opacity ?? 0.9);
    setLogoSize(channel?.logo_size_pct ?? 14);
    setLogoSafeMargin(channel?.logo_safe_margin_pct ?? 0);
    setBrandPrimary(channel?.brand_primary_color || "#7C3AED");
    setBrandSecondary(channel?.brand_secondary_color || "#111827");
    setBrandText(channel?.brand_text_color || "#FFFFFF");
    setDaypartsOn(!!channel?.logo_dayparts_enabled);
    setOvernightStart(channel?.overnight_start || "00:00");
    setOvernightEnd(channel?.overnight_end || "06:00");
    setOvernightOpacity(channel?.logo_overnight_opacity ?? 0.5);
    setOvnPreviewUntil(channel?.logo_force_overnight_until || null);
    setScte35(channel?.scte35_enabled ?? true);
    setLiveSourceMode(channel?.live_source_mode || "mp4");
    setPublicEmbed(!!channel?.public_embed_enabled);
    setInspect(null);
    setRevealedSecret(null);
  }, [channel?.id]);

  if (!channel) return null;

  const saveBrand = async () => {
    if (!brandName.trim()) { toast.error("Brand name is required"); return; }
    setSavingBrand(true);
    try {
      const r = await api.patch(`/channels/${channel.id}`, {
        name: brandName.trim(),
        color: brandColor,
        logo_position: logoPos,
        logo_opacity: Number(logoOpacity),
        logo_size_pct: Number(logoSize),
        logo_safe_margin_pct: Number(logoSafeMargin),
        brand_primary_color: brandPrimary,
        brand_secondary_color: brandSecondary,
        brand_text_color: brandText,
        song_flash_count: Number(flashCount),
        song_flash_duration_sec: Number(flashDur),
        song_flash_style: flashStyle,
        logo_dayparts_enabled: daypartsOn,
        overnight_start: overnightStart,
        overnight_end: overnightEnd,
        logo_overnight_opacity: Number(overnightOpacity),
      });
      onSaved(r.data);
      toast.success("Brand updated");
    } catch {
      toast.error("Failed to update brand");
    }
    setSavingBrand(false);
  };

  const forceOvernight = async (minutes) => {
    setOvnPreviewBusy(true);
    try {
      const r = await api.post(`/channels/${channel.id}/logo/force-overnight`, { minutes });
      setOvnPreviewUntil(r.data.force_overnight_until);
      if (minutes === 0) toast.success("Overnight preview cleared — back to daypart schedule");
      else toast.success(`Overnight bug forced on-air for ${minutes} min · ${r.data.restarted} push(es) restarting`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setOvnPreviewBusy(false);
  };


  const onLogoFile = async (e, variant = "primary") => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/channels/${channel.id}/logo?variant=${variant}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved(r.data);
      setLogoVersion((v) => v + 1);
      toast.success(variant === "overnight" ? "Overnight logo uploaded" : "Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    }
    setLogoBusy(false);
    if (logoFileRef.current) logoFileRef.current.value = "";
    if (overnightFileRef.current) overnightFileRef.current.value = "";
  };

  const removeLogo = async (variant = "primary") => {
    setLogoBusy(true);
    try {
      const r = await api.delete(`/channels/${channel.id}/logo?variant=${variant}`);
      onSaved(r.data);
      toast.success("Logo removed");
    } catch {
      toast.error("Failed to remove logo");
    }
    setLogoBusy(false);
  };

  const patchAp = (k, v) => setAp((p) => ({ ...p, [k]: v }));
  const patchCdn = (k, v) => setCdn((p) => ({ ...p, [k]: v }));
  const patchTrack = (i, patch) =>
    setAp((p) => ({
      ...p,
      language_tracks: (p.language_tracks || []).map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
    }));
  const addTrack = () =>
    setAp((p) => ({
      ...p,
      language_tracks: [
        ...(p.language_tracks || []),
        { id: crypto.randomUUID?.() || `t${Date.now()}`, code: "und", name: "New track", layout: "2.0", delivery_codec: "AAC-LC", delivery_bitrate_kbps: 192, dialnorm: -24, is_primary: false, is_descriptive: false },
      ],
    }));
  const removeTrack = (i) =>
    setAp((p) => ({ ...p, language_tracks: (p.language_tracks || []).filter((_, idx) => idx !== i) }));

  const rotateCdnSecret = async () => {
    if (!window.confirm("Rotate CDN origin secret via KMS? This invalidates the old credential.")) return;
    setRotating(true);
    try {
      await api.post(`/channels/${channel.id}/cdn/rotate`, { reason: "manual-from-console" });
      const rev = await api.get(`/channels/${channel.id}/cdn/rotate/reveal`);
      setRevealedSecret(rev.data);
      await settings.refreshRotations();
      toast.success("CDN secret rotated · new version issued");
    } catch {
      toast.error("Rotation failed");
    }
    setRotating(false);
  };
  const revealCdnSecret = async () => {
    try {
      const rev = await api.get(`/channels/${channel.id}/cdn/rotate/reveal`);
      setRevealedSecret(rev.data);
    } catch {
      toast.error("Reveal failed");
    }
  };
  const migrateTo = async (targetNodeId) => {
    if (!window.confirm(`Blue/green migrate to ${targetNodeId}?`)) return;
    setMigrating(true);
    try {
      await api.post(`/channels/${channel.id}/engine/migrate`, {
        target_node_id: targetNodeId,
        mode: "blue_green",
        overlap_sec: 6,
        reason: "manual-from-console",
      });
      toast.success(`Migrating → ${targetNodeId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    }
    setMigrating(false);
  };
  const triggerFailover = async (region) => {
    try {
      await api.post(`/channels/${channel.id}/cdn/failover`, { region });
      toast.success(`Failover → ${region}`);
    } catch {
      toast.error("Failover failed");
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/channels/${channel.id}`, {
        preview_hls_url: url,
        demo_preview_hls_url: demoUrl || null,
        gap_fallback_category_id: gapCat || null,
        no_repeat_window_min: Math.max(0, parseInt(noRepeatMin, 10) || 0),
        audio_profile: ap,
        cdn,
        hdr_mode: hdrMode,
        dolby_vision: dv,
        hlg,
        engine,
        scte35_enabled: scte35,
        live_source_mode: liveSourceMode,
        public_embed_enabled: publicEmbed,
      });
      onSaved(r.data);
      toast.success("Channel updated");
      onClose();
    } catch {
      toast.error("Failed to update channel");
    }
    setSaving(false);
  };
  const inspectBitstream = async () => {
    setInspecting(true);
    try {
      const r = await api.get(`/audio/inspect/${channel.id}`);
      setInspect(r.data);
    } catch {
      toast.error("Bitstream inspection failed");
    }
    setInspecting(false);
  };
  const togglePublish = async () => {
    const prev = cdn.publish_state;
    const next = prev === "live" ? "idle" : "live";
    setPubBusy(true);
    setCdn((c) => ({ ...c, publish_state: next })); // optimistic
    try {
      const r = await api.post(`/channels/${channel.id}/publish`, { publish_state: next });
      setCdn(cdnOf(r.data));
      onSaved(r.data);
      toast.success(next === "live" ? "Channel is now LIVE — publishing" : "Channel set to IDLE — worker stopped");
    } catch (e) {
      setCdn((c) => ({ ...c, publish_state: prev })); // revert on error
      toast.error(e?.response?.data?.detail || "Publish toggle failed");
    } finally {
      setPubBusy(false);
    }
  };
  const isAC3 = ap.delivery_codec === "AC-3" || ap.delivery_codec === "E-AC-3";

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]"
        data-testid="channel-settings-dialog"
      >
        <DialogHeader className="relative pb-4 mb-2">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display">Channel · {channel.name}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Configure HLS input, broadcast audio profile, HDR mode, multi-region CDN routing, engine adapter and KMS credential rotation for this channel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 min-w-0">
          {/* Publish state — prominent Live/Idle control (top of modal) */}
          <div
            className="border rounded-sm p-3 space-y-3"
            data-testid="channel-publish-state-panel"
            style={{ borderColor: cdn.publish_state === "live" ? "rgba(16,185,129,0.5)" : "rgba(120,120,130,0.35)" }}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Publish state</div>
            <div className="flex items-center justify-between gap-3">
              {cdn.publish_state === "live" ? (
                <div className="flex items-center gap-2 font-mono text-sm font-semibold text-emerald-400" data-testid="publish-state-badge">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  ● LIVE — publishing continuously
                </div>
              ) : (
                <div className="flex items-center gap-2 font-mono text-sm font-semibold text-zinc-400" data-testid="publish-state-badge">
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-500" />
                  ○ IDLE — worker stopped
                </div>
              )}
              <Button
                onClick={togglePublish}
                disabled={pubBusy}
                data-testid="publish-state-toggle"
                className={`rounded-sm shrink-0 ${cdn.publish_state === "live" ? "bg-zinc-700 hover:bg-zinc-600 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}
              >
                {pubBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (cdn.publish_state === "live" ? "Set to Idle" : "Set to Live")}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              When Live, this channel runs a continuous publish worker that pushes to CDN and generates HLS segments. When Idle, the worker is stopped and no CPU is consumed. Idle channels keep all their schedule and asset data intact and can be resumed at any time.
            </p>
          </div>

          {/* Broadcast & distribution — SCTE-35, live source, public embed */}
          <div className="border border-border rounded-sm p-3 space-y-3" data-testid="channel-broadcast-panel">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Broadcast &amp; distribution
            </div>

            {/* SCTE-35 toggle */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm">SCTE-35 ad markers</Label>
                <p className="text-[11px] text-muted-foreground">Insert CUE-OUT/CUE-IN splice markers for ad breaks.</p>
              </div>
              <Switch checked={scte35} onCheckedChange={setScte35} data-testid="channel-scte35-toggle" />
            </div>

            {/* Live source mode */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm">Live feed source</Label>
                <p className="text-[11px] text-muted-foreground">What the Flussonic pusher pulls from R2. HLS uses the adaptive ladder (when transcoded).</p>
              </div>
              <div className="flex rounded-sm border border-border overflow-hidden shrink-0" data-testid="channel-live-source-mode">
                {["mp4", "hls"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setLiveSourceMode(m)}
                    data-testid={`channel-live-source-${m}`}
                    className={`px-3 py-1.5 text-xs font-mono uppercase transition-colors ${liveSourceMode === m ? "bg-fuchsia-500/20 text-fuchsia-300" : "text-muted-foreground hover:bg-muted/40"}`}
                  >
                    {m === "mp4" ? "MP4" : "HLS ladder"}
                  </button>
                ))}
              </div>
            </div>

            {/* Public embed */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm">Public web player</Label>
                <p className="text-[11px] text-muted-foreground">Share a no-login page that streams this channel.</p>
              </div>
              <Switch checked={publicEmbed} onCheckedChange={setPublicEmbed} data-testid="channel-public-embed-toggle" />
            </div>
            {publicEmbed && (
              <div className="flex items-center gap-2 bg-black/30 rounded-sm border border-border px-2 py-1.5 min-w-0" data-testid="channel-watch-url-row">
                <code className="text-[11px] font-mono text-cyan-300 truncate flex-1 min-w-0" data-testid="channel-watch-url">{watchUrl}</code>
                <Button
                  size="sm" variant="outline"
                  className="rounded-sm h-7 text-[11px] shrink-0"
                  onClick={() => {
                    try {
                      navigator.clipboard?.writeText(watchUrl)?.then?.(
                        () => toast.success("Link copied"),
                        () => toast.success("Link ready — press Ctrl/Cmd+C"),
                      );
                    } catch (_) {
                      toast.success("Link ready — copy it manually");
                    }
                  }}
                  data-testid="channel-watch-copy-btn"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
                <a href={watchUrl} target="_blank" rel="noreferrer" data-testid="channel-watch-open-btn"
                   className="rounded-sm h-7 px-2 grid place-items-center border border-border hover:bg-muted/40 shrink-0">
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground font-mono">Save the channel to apply these changes.</p>
          </div>

          {canManageBranding && (
            <div className="border border-border rounded-sm p-3" data-testid="channel-branding-panel">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                Brand identity &amp; overlay graphics
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1 min-w-0">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Brand name</Label>
                  <Input
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    className="mt-1 rounded-sm font-display"
                    placeholder="e.g. AFRO MUSIC HD"
                    data-testid="channel-brand-name-input"
                  />
                </div>
                <div className="shrink-0">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Colour</Label>
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="mt-1 h-9 w-12 rounded-sm bg-transparent border border-border cursor-pointer block p-0.5"
                    data-testid="channel-brand-color-input"
                  />
                </div>
                <Button
                  onClick={saveBrand}
                  disabled={savingBrand || !brandName.trim()}
                  className="rounded-sm tenant-btn shrink-0"
                  data-testid="channel-brand-save"
                >
                  {savingBrand ? "Saving…" : "Save brand"}
                </Button>
              </div>

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">14-layer overlay graphics</div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Logos, lower-thirds, tickers, clocks &amp; text — previewed on air and burned into the live stream.</p>
                </div>
                <Button variant="outline" onClick={() => setGfxOpen(true)} className="rounded-sm shrink-0 text-xs" data-testid="channel-graphics-editor-btn">
                  Open editor
                </Button>
              </div>

              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  Logo / on-air bug
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 shrink-0 rounded-sm border border-border bg-[repeating-conic-gradient(#1a1a1a_0%_25%,#111_0%_50%)] bg-[length:16px_16px] flex items-center justify-center overflow-hidden" data-testid="channel-logo-preview">
                    {channel.logo_url ? (
                      <img
                        src={`${API}${channel.logo_url}?auth=${encodeURIComponent(getToken() || "")}&v=${logoVersion}`}
                        alt="logo"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-[9px] text-muted-foreground uppercase tracking-widest">No logo</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex gap-2">
                      <input ref={logoFileRef} type="file" accept="image/png,image/webp,image/gif,image/apng,image/svg+xml,image/jpeg" onChange={onLogoFile} className="hidden" data-testid="channel-logo-file" />
                      {isSuperAdmin ? (
                        <>
                          <Button variant="outline" onClick={() => logoFileRef.current?.click()} disabled={logoBusy} className="rounded-sm text-xs" data-testid="channel-logo-upload-btn">
                            {logoBusy ? "Uploading…" : (channel.logo_url ? "Replace logo" : "Upload logo")}
                          </Button>
                          {channel.logo_url && (
                            <Button variant="outline" onClick={removeLogo} disabled={logoBusy} className="rounded-sm text-xs text-red-400 hover:text-red-300" data-testid="channel-logo-remove-btn">
                              Remove
                            </Button>
                          )}
                        </>
                      ) : (
                        <span className="text-[9px] text-muted-foreground self-center">Logo upload is managed by the super admin. You can still adjust placement, brand colors, flash timing and presets below.</span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Corner</Label>
                        <select value={logoPos} onChange={(e) => setLogoPos(e.target.value)} className="mt-1 w-full h-8 rounded-sm bg-background border border-border text-xs px-1" data-testid="channel-logo-position">
                          <option value="top-left">Top left</option>
                          <option value="top-right">Top right</option>
                          <option value="bottom-left">Bottom left</option>
                          <option value="bottom-right">Bottom right</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Size {Math.round(logoSize)}%</Label>
                        <input type="range" min="5" max="40" step="1" value={logoSize} onChange={(e) => setLogoSize(e.target.value)} className="mt-2 w-full accent-fuchsia-500" data-testid="channel-logo-size" />
                      </div>
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Opacity {Math.round(logoOpacity * 100)}%</Label>
                        <input type="range" min="0.2" max="1" step="0.05" value={logoOpacity} onChange={(e) => setLogoOpacity(e.target.value)} className="mt-2 w-full accent-fuchsia-500" data-testid="channel-logo-opacity" />
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Title-safe margin</Label>
                      <div className="mt-1 grid grid-cols-3 gap-1" data-testid="channel-logo-safe-margin">
                        {[
                          { v: 0, label: "Flush", hint: "edge (24px min)" },
                          { v: 5, label: "Action-safe", hint: "5%" },
                          { v: 10, label: "Title-safe", hint: "10%" },
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => setLogoSafeMargin(opt.v)}
                            data-testid={`channel-logo-safe-${opt.v}`}
                            className={`text-[10px] font-mono px-2 py-1.5 rounded-sm border text-left ${
                              Number(logoSafeMargin) === opt.v
                                ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-500"
                                : "border-border text-muted-foreground hover:bg-muted/40"
                            }`}
                          >
                            <div className="font-bold uppercase tracking-widest">{opt.label}</div>
                            <div className="text-[8px] opacity-70">{opt.hint}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-2" data-testid="channel-brand-colors">
                      <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Brand graphics colors (auto-extracted from logo · override here)</Label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        {[
                          { label: "Primary", val: brandPrimary, set: setBrandPrimary, tid: "brand-primary" },
                          { label: "Secondary", val: brandSecondary, set: setBrandSecondary, tid: "brand-secondary" },
                          { label: "Text", val: brandText, set: setBrandText, tid: "brand-text" },
                        ].map((c) => (
                          <div key={c.tid} className="flex items-center gap-1.5">
                            <input type="color" value={c.val} onChange={(e) => c.set(e.target.value)} className="h-8 w-8 rounded-sm border border-border bg-transparent cursor-pointer" data-testid={`channel-${c.tid}-swatch`} />
                            <div className="min-w-0">
                              <div className="text-[8px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
                              <input value={c.val} onChange={(e) => c.set(e.target.value)} className="w-full h-5 text-[10px] font-mono rounded-sm bg-background border border-border px-1" data-testid={`channel-${c.tid}-hex`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3" data-testid="overlay-preview">
                      <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">On-air preview (badge · logo · lower-third)</Label>
                      <div className="mt-1 relative w-full aspect-video rounded-sm overflow-hidden border border-border bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
                        <div className="absolute inset-0 flex items-center justify-center text-zinc-700 text-4xl font-display font-black tracking-tight select-none">SAMPLE FRAME</div>
                        {/* Logo bug */}
                        {channel?.logo_url && (
                          <img
                            src={`${BACKEND_URL}${channel.logo_url}?v=${logoVersion}`}
                            alt=""
                            style={{
                              position: "absolute",
                              width: `${logoSize}%`,
                              opacity: Number(logoOpacity),
                              [logoPos.includes("top") ? "top" : "bottom"]: `${Math.max(2, Number(logoSafeMargin) || 3)}%`,
                              [logoPos.includes("left") ? "left" : "right"]: `${Math.max(2, Number(logoSafeMargin) || 3)}%`,
                            }}
                          />
                        )}
                        {/* Category badge (top-left, whole clip) */}
                        <div
                          className="absolute top-[6%] left-[4%] rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-lg"
                          style={{ background: brandPrimary, color: brandText }}
                          data-testid="overlay-preview-badge"
                        >
                          {livePreview?.category || "AFRO POP"}
                        </div>
                        {/* Song lower-third (bottom-left, first 15s) */}
                        <div
                          className="absolute bottom-[7%] left-[4%] rounded px-2.5 py-1 text-[11px] font-semibold shadow-lg"
                          style={{ background: brandPrimary, color: brandText, opacity: 0.85, maxWidth: "82%" }}
                          data-testid="overlay-preview-lowerthird"
                        >
                          {livePreview?.song || "Aya Nakamura — Djadja — 2018"}
                        </div>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">Shows this channel’s current on-air category + song (falls back to sample text when idle). Lower-third flashes twice on-air: 10–20s in, and again 10s before the clip ends.</p>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3" data-testid="flash-controls">
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Song flashes / clip</Label>
                        <div className="mt-1 flex gap-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button" onClick={() => setFlashCount(n)}
                              data-testid={`flash-count-${n}`}
                              className={`flex-1 h-8 text-xs font-mono rounded-sm border ${Number(flashCount) === n ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-500" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Each flash: {flashDur}s</Label>
                        <input type="range" min="5" max="30" step="1" value={flashDur}
                          onChange={(e) => setFlashDur(Number(e.target.value))}
                          data-testid="flash-duration"
                          className="mt-2 w-full accent-fuchsia-500" />
                      </div>
                    </div>
                    <div className="mt-2" data-testid="flash-style">
                      <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Lower-third transition</Label>
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        {[{ v: "fade", l: "Fade" }, { v: "slide", l: "Slide in" }].map((o) => (
                          <button key={o.v} type="button" onClick={() => setFlashStyle(o.v)}
                            data-testid={`flash-style-${o.v}`}
                            className={`h-8 text-xs font-mono rounded-sm border ${flashStyle === o.v ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-500" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3" data-testid="overlay-presets">
                      <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Overlay presets (brand + logo + flash)</Label>
                      <div className="mt-1 flex gap-1">
                        <input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Preset name" className="flex-1 h-8 text-xs rounded-sm bg-background border border-border px-2" data-testid="preset-name-input" />
                        <Button type="button" variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={savePreset} data-testid="preset-save-btn">Save preset</Button>
                      </div>
                      {presets.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {presets.map((p) => (
                            <span key={p.id} className={`relative group/preset inline-flex items-center gap-1 text-[10px] font-mono rounded-sm border bg-muted/30 pl-2 pr-1 py-0.5 ${p.is_default ? "border-amber-400/70" : "border-border"}`} data-testid={`preset-chip-${p.id}`}>
                              {/* Hover mini mockup — live badge + lower-third in the preset's brand colors */}
                              <span
                                className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 hidden group-hover/preset:block"
                                data-testid={`preset-preview-${p.id}`}
                              >
                                <span className="block w-44 rounded-sm border border-border bg-popover shadow-2xl overflow-hidden">
                                  <span className="block relative w-full aspect-video bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
                                    <span className="absolute inset-0 flex items-center justify-center text-zinc-700 text-[9px] font-display font-black tracking-tight select-none">SAMPLE FRAME</span>
                                    {/* logo bug indicator at the preset corner */}
                                    <span
                                      className="absolute rounded-[2px] border border-white/40"
                                      style={{
                                        width: `${Math.max(8, (p.logo_size_pct || 14))}%`,
                                        aspectRatio: "16 / 9",
                                        background: "rgba(255,255,255,0.14)",
                                        opacity: p.logo_opacity ?? 0.9,
                                        [(p.logo_position || "top-right").includes("top") ? "top" : "bottom"]: "6%",
                                        [(p.logo_position || "top-right").includes("left") ? "left" : "right"]: "5%",
                                      }}
                                    />
                                    {/* category badge (top-left) */}
                                    <span
                                      className="absolute top-[7%] left-[4%] rounded px-1 py-0.5 text-[7px] font-bold uppercase tracking-wide shadow"
                                      style={{ background: p.brand_primary_color || "#7C3AED", color: p.brand_text_color || "#fff" }}
                                    >
                                      AFRO POP
                                    </span>
                                    {/* song lower-third (bottom-left) */}
                                    <span
                                      className="absolute bottom-[8%] left-[4%] rounded px-1.5 py-0.5 text-[7px] font-semibold shadow"
                                      style={{ background: p.brand_primary_color || "#7C3AED", color: p.brand_text_color || "#fff", opacity: 0.9, maxWidth: "82%" }}
                                    >
                                      Aya Nakamura — Djadja
                                    </span>
                                  </span>
                                  <span className="flex items-center justify-between px-1.5 py-1 text-[8px] font-mono uppercase tracking-widest text-muted-foreground bg-black/40">
                                    <span className="truncate">{p.name}</span>
                                    <span>{p.song_flash_count ?? 2}×{p.song_flash_duration_sec ?? 10}s</span>
                                  </span>
                                </span>
                              </span>
                              <button type="button" onClick={() => applyPreset(p)} className="hover:text-fuchsia-500" data-testid={`preset-apply-${p.id}`}>
                                <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: p.brand_primary_color || "#666" }} />{p.name}
                              </button>
                              <button type="button" onClick={() => toggleDefault(p)} className={`px-0.5 ${p.is_default ? "text-amber-400" : "text-muted-foreground hover:text-amber-400"}`} title={p.is_default ? "House default — new channels inherit this. Click to clear." : "Set as default for new channels"} data-testid={`preset-default-${p.id}`}>{p.is_default ? "★" : "☆"}</button>
                              <button type="button" onClick={() => applyPresetAll(p)} className="text-[8px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 px-1" title="Apply to all channels" data-testid={`preset-apply-all-${p.id}`}>all</button>
                              <button type="button" onClick={() => deletePreset(p)} className="text-muted-foreground hover:text-red-400" data-testid={`preset-del-${p.id}`}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-[9px] text-muted-foreground">PNG/WEBP/SVG · ≤3 MB · click "Save brand" to apply position/size/opacity/safe-margin. Safe margin keeps the on-air bug off the frame edge (broadcast title-safe = 10%).</p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border/60">
                  <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest cursor-pointer" data-testid="channel-daypart-toggle-label">
                    <input type="checkbox" checked={daypartsOn} onChange={(e) => setDaypartsOn(e.target.checked)} data-testid="channel-daypart-toggle" />
                    Overnight daypart bug
                  </label>
                  {daypartsOn && (
                    <div className="mt-2 flex items-start gap-3">
                      <div className="w-16 h-16 shrink-0 rounded-sm border border-border bg-[repeating-conic-gradient(#1a1a1a_0%_25%,#111_0%_50%)] bg-[length:14px_14px] flex items-center justify-center overflow-hidden">
                        {channel.logo_overnight_url ? (
                          <img src={`${API}${channel.logo_overnight_url}&auth=${encodeURIComponent(getToken() || "")}&v=${logoVersion}`} alt="overnight" className="max-w-full max-h-full object-contain" />
                        ) : (
                          <span className="text-[8px] text-muted-foreground uppercase">reuse day</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex gap-2">
                          <input ref={overnightFileRef} type="file" accept="image/png,image/webp,image/gif,image/apng,image/svg+xml,image/jpeg" onChange={(e) => onLogoFile(e, "overnight")} className="hidden" data-testid="channel-overnight-file" />
                          {isSuperAdmin ? (
                            <>
                              <Button variant="outline" onClick={() => overnightFileRef.current?.click()} disabled={logoBusy} className="rounded-sm text-xs" data-testid="channel-overnight-upload-btn">
                                {channel.logo_overnight_url ? "Replace overnight logo" : "Upload overnight logo"}
                              </Button>
                              {channel.logo_overnight_url && (
                                <Button variant="outline" onClick={() => removeLogo("overnight")} disabled={logoBusy} className="rounded-sm text-xs text-red-400" data-testid="channel-overnight-remove-btn">Remove</Button>
                              )}
                            </>
                          ) : (
                            <span className="text-[9px] text-muted-foreground self-center">Overnight logo upload is managed by the super admin.</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">From (UTC)</Label>
                            <Input type="time" value={overnightStart} onChange={(e) => setOvernightStart(e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid="channel-overnight-start" />
                          </div>
                          <div>
                            <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">To (UTC)</Label>
                            <Input type="time" value={overnightEnd} onChange={(e) => setOvernightEnd(e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid="channel-overnight-end" />
                          </div>
                          <div>
                            <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Opacity {Math.round(overnightOpacity * 100)}%</Label>
                            <input type="range" min="0.1" max="1" step="0.05" value={overnightOpacity} onChange={(e) => setOvernightOpacity(e.target.value)} className="mt-2 w-full accent-fuchsia-500" data-testid="channel-overnight-opacity" />
                          </div>
                        </div>
                        <p className="text-[9px] text-muted-foreground">Overnight window uses this logo (or the day logo, dimmed) at the opacity above.</p>
                        <div className="mt-2 pt-2 border-t border-border flex flex-wrap items-center gap-2" data-testid="ovn-preview-block">
                          <Button
                            variant="outline" size="sm"
                            className="rounded-sm h-8 text-xs border-cyan-500/40 text-cyan-400"
                            onClick={() => forceOvernight(15)}
                            disabled={ovnPreviewBusy}
                            data-testid="ovn-preview-btn"
                            title="Force the overnight bug on-air for 15 minutes so you can verify it in Flussonic"
                          >
                            {ovnPreviewBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Moon className="w-3.5 h-3.5 mr-1" />} Preview overnight now (15 min)
                          </Button>
                          {ovnPreviewUntil && new Date(ovnPreviewUntil) > new Date() && (
                            <>
                              <span className="text-[10px] font-mono text-cyan-400" data-testid="ovn-preview-active">
                                ● forced until {new Date(ovnPreviewUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                              <Button
                                variant="outline" size="sm"
                                className="rounded-sm h-8 text-xs text-red-400 border-red-500/40"
                                onClick={() => forceOvernight(0)}
                                disabled={ovnPreviewBusy}
                                data-testid="ovn-preview-stop-btn"
                              >
                                Stop preview
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <ObsIngestPanel channel={channel} onUpdated={(c) => { onSaved?.(c); setUrl(c?.preview_hls_url || ""); }} />
          <CustomCdnPanel channel={channel} onUpdated={(c) => { onSaved?.(c); }} />
          <HlsInputPanel url={url} setUrl={setUrl} demoUrl={demoUrl} setDemoUrl={setDemoUrl} />
          <div className="panel p-4 space-y-2" data-testid="gap-fallback-panel">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Gap fallback category</Label>
            <p className="text-[11px] text-muted-foreground">When nothing is scheduled right now, loop a ready clip from this category instead of the demo pattern.</p>
            <select
              data-testid="gap-fallback-category-select"
              value={gapCat}
              onChange={(e) => setGapCat(e.target.value)}
              className="w-full rounded-sm bg-background border border-border text-sm px-2 py-2 font-mono"
            >
              <option value="">— None (use demo URL) —</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="panel p-4 space-y-2" data-testid="no-repeat-panel">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">No-repeat window (minutes)</Label>
            <p className="text-[11px] text-muted-foreground">Don't re-air the same song — or any song by an artist already aired — on this channel within this many minutes. Set 0 to turn the rule off. If every song in a category is still inside the window, the one aired longest ago plays so the channel never goes silent.</p>
            <input
              type="number"
              min={0}
              step={5}
              data-testid="no-repeat-window-input"
              value={noRepeatMin}
              onChange={(e) => setNoRepeatMin(e.target.value)}
              className="w-32 rounded-sm bg-background border border-border text-sm px-2 py-2 font-mono"
            />
          </div>
          <AudioProfilePanel ap={ap} setAp={setAp} patchAp={patchAp} isAC3={isAC3} channel={channel} />
          <LanguageTracksPanel ap={ap} addTrack={addTrack} patchTrack={patchTrack} removeTrack={removeTrack} />
          <OutputSignalsPanel
            channel={channel} hdrMode={hdrMode} setHdrMode={setHdrMode}
            cdn={cdn} patchCdn={patchCdn} dv={dv} setDv={setDv} hlg={hlg} setHlg={setHlg}
            planLimits={settings.planLimits} togglePublish={togglePublish}
          />
          <CdnPipelinePanel
            cdn={cdn}
            pipelineHealth={settings.pipelineHealth}
            pipelineSegments={settings.pipelineSegments}
          />
          <CdnRegionsPanel cdn={cdn} regionsData={settings.regionsData} triggerFailover={triggerFailover} />
          <EngineClusterPanel
            engine={engine} engineNodes={settings.engineNodes}
            migrations={settings.migrations} migrating={migrating} migrateTo={migrateTo}
          />
          <KmsRotationPanel
            cdn={cdn} kmsBackends={settings.kmsBackends}
            rotations={settings.rotations} rotating={rotating}
            revealedSecret={revealedSecret} rotateCdnSecret={rotateCdnSecret} revealCdnSecret={revealCdnSecret}
          />
          <EngineAdapterPanel
            engine={engine} setEngine={setEngine}
            engineAdapters={settings.engineAdapters}
            engineHello={settings.engineHello} engineStatus={settings.engineStatus}
          />
          <Ac3InspectorPanel inspect={inspect} inspecting={inspecting} inspectBitstream={inspectBitstream} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-border">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="channel-hls-url-save" className="rounded-sm tenant-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {canManageBranding && channel && (
      <GraphicsEditorDialog open={gfxOpen} onClose={() => setGfxOpen(false)} channel={channel} />
    )}
    </>
  );
}
